import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { KYDAdminEvent } from "@common/interfaces/KYDEvent";
import { BodyDoc } from "@common/interfaces/KYDCampaign";
import { EditModalType } from "../../../utils";
import { useForm } from "react-hook-form";
import TipTapEditor from "../../../ui/editor/TipTapEditor";
import { useRef, useEffect } from "react";
import { Editor } from "@tiptap/react";
import markdownIt from "markdown-it";
import { defaultBubbleMenuConfig } from "src/ui/editor";

const md = markdownIt({
  breaks: true,
});

const EditDescriptionModal = ({
  setModalType,
  isLoading = false,
  isOpen = false,
  data,
  onSave,
}: {
  isLoading: boolean;
  isOpen?: boolean;
  data?: Pick<KYDAdminEvent, "description" | "description_rich">;
  onSave: (values: { description_rich: BodyDoc }) => void;
  setModalType: (modalType: EditModalType) => void;
}) => {
  const editorRef = useRef<Editor>(null);

  const { watch, setValue, handleSubmit } = useForm({
    criteriaMode: "all",
    reValidateMode: "onChange",
    mode: "all",
    values: {
      description_rich: data?.description_rich || {
        type: "doc" as const,
        content: [],
      },
    },
  });
  const description_rich = watch("description_rich");

  // Set editor content when component mounts or data changes
  useEffect(() => {
    if (!editorRef.current) return;

    if (data?.description_rich) {
      editorRef.current.commands.setContent(data.description_rich);
    } else if (data?.description) {
      const rendered = md.render(data?.description || "");
      editorRef.current.commands.setContent(rendered);
    }
  }, [data?.description_rich, isOpen, data?.description, editorRef.current]);

  const _onSave = (values: { description_rich: BodyDoc }) => {
    onSave({ description_rich });
  };

  return (
    <Modal
      size={["full", "3xl"]}
      isOpen={isOpen}
      onClose={() => setModalType(EditModalType.INACTIVE)}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Description</ModalHeader>
        <ModalBody>
          <Box
            minH={"400px"}
            borderWidth={1}
            borderColor={"gray.300"}
            borderRadius={"md"}
          >
            <TipTapEditor
              ref={editorRef}
              bubbleMenuConfig={{
                ...defaultBubbleMenuConfig,
                showColorSelector: false,
              }}
              value={description_rich}
              onChange={(value) => {
                setValue("description_rich", value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              isDisabled={isLoading}
            />
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={isLoading}
            onClick={() => {
              setModalType(EditModalType.INACTIVE);
            }}
            mr="4"
          >
            Cancel
          </Button>
          <Button
            color="white"
            bg="green.500"
            isLoading={isLoading}
            onClick={handleSubmit(_onSave)}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditDescriptionModal;
